import { localizePath, type Locale } from "@thyra/landing-i18n";

/** Site-relative blog paths and slug rules, shared by pages, RSS, sitemap, llms. */
export const blogPostPath = (slug: string) => `/blog/${slug}`;
export const blogTagPath = (slug: string) => `/blog/tag/${slug}`;
export const BLOG_INDEX_PATH = "/blog";
export const BLOG_RSS_PATH = "/blog/rss.xml";

/** Absolute URL of an unprefixed blog path in `locale` (no trailing slash). */
export function blogUrl(siteUrl: string, path: string, locale: Locale): string {
  return `${siteUrl.replace(/\/$/, "")}${localizePath(path, locale)}`;
}

/** A tag page is only worth indexing with at least this many posts. */
export const MIN_INDEXABLE_TAG_POSTS = 3;

/** Slugs that would collide with real files or routes under /blog. */
export const RESERVED_SLUGS: ReadonlySet<string> = new Set([
  "tag",
  "rss.xml",
  "page",
  "__shell",
  "__shell.html",
]);

const SAFE_SLUG = /^[a-z0-9][a-z0-9-]*$/i;

/**
 * Only plain slugs become files/URLs: this keeps `..`, separators and
 * encoded tricks out of output paths, and keeps pages, RSS, sitemap and llms
 * consistent (they all filter through here).
 */
export function isPublishableSlug(slug: string): boolean {
  return SAFE_SLUG.test(slug) && !RESERVED_SLUGS.has(slug.toLowerCase());
}

/** Keeps entries with a publishable slug; reports each skipped one. */
export function keepPublishable<T extends { slug: string }>(
  items: T[],
  onSkip?: (slug: string) => void,
): T[] {
  return items.filter((item) => {
    if (isPublishableSlug(item.slug)) return true;
    onSkip?.(item.slug);
    return false;
  });
}
